import "./NavigationSubMenu.scss";
import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FocusTrap } from "../../../common/components/FocusTrap/FocusTrap";
import { IconCarrot } from "../../../common/icons/IconCarrot/IconCarrot";
import { IconClose } from "../../../common/icons/IconClose/IconClose";
import type { NavItem } from "../types";
import { isRouteActive } from "../../../common/util/Navigation/routeMatching";
import defaultCommonContent from "../../../content/common";

interface NavigationSubMenuProps {
  title: string;
  sectionId: string;
  navItems: NavItem[];
  onBack: () => void;
  onClose: () => void;
  onActivate: (sectionId: string) => void;
}

export const NavigationSubMenu = ({
  title,
  sectionId,
  navItems,
  onBack,
  onClose,
  onActivate,
}: NavigationSubMenuProps) => {
  const [isClosing, setIsClosing] = useState(false);
  const { pathname, hash } = useLocation();
  const { t } = useTranslation(["navigation", "common"]);

  const disclaimerSymbol = t("disclaimerSymbol", defaultCommonContent["disclaimerSymbol"], { ns: "common" });

  const handleBack = useCallback(() => {
    setIsClosing(true);
  }, []);

  const handleItemClick = useCallback(() => {
    onActivate(sectionId);
    onClose();
  }, [onClose, onActivate, sectionId]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleAnimationEnd = useCallback(
    (event: React.AnimationEvent<HTMLElement>) => {
      if (isClosing && event.animationName === "slideOutToLeft" && event.target === event.currentTarget) {
        onBack();
      }
    },
    [isClosing, onBack],
  );

  const isItemActive = (item: NavItem) => isRouteActive(item.path, pathname, hash);

  const menuClass = `NavigationSubMenu${isClosing ? " NavigationSubMenu--closing" : ""}`;

  return (
    <FocusTrap
      handleOnClose={handleClose}
      className={menuClass}
      role="dialog"
      aria-modal={true}
      aria-label={`${title} submenu`}
      onAnimationEnd={handleAnimationEnd}
      data-testid="navigation-sub-menu"
    >
      <div className="NavigationSubMenu__header">
        <button
          type="button"
          className="NavigationSubMenu__back-button"
          onClick={handleBack}
          aria-label="Back"
          data-testid="sub-menu-back"
        >
          <IconCarrot orientation="left" color="white" />
        </button>
        <span className="NavigationSubMenu__title">{title}</span>
        <button
          type="button"
          className="NavigationSubMenu__close-button"
          onClick={handleClose}
          aria-label="Close navigation"
          data-testid="sub-menu-close"
        >
          <IconClose width="1.5rem" height="1.5rem" color="white" />
        </button>
      </div>

      <div className="NavigationSubMenu__content">
        <div className="NavigationSubMenu__section">
          {navItems.map((item) => {
            const [itemPathname, itemHash] = item.path.split("#");
            return (
              <Link
                key={item.id}
                to={{ pathname: itemPathname, hash: itemHash ? `#${itemHash}` : undefined }}
                className={`NavigationSubMenu__item${isItemActive(item) ? " NavigationSubMenu__item--active" : ""}`}
                data-testid={`sub-menu-item-${item.id}`}
                onClick={handleItemClick}
              >
                {t(`linkTitle.${item.contentKey ?? item.id}`, item.label, { symbol: disclaimerSymbol })}
              </Link>
            );
          })}
        </div>
      </div>
    </FocusTrap>
  );
};
